"use client";

/**
 * ZoomableImage
 *
 * A Next.js <Image> wrapper that opens a full-size lightbox on click.
 *
 * Usage:
 * -------
 * import { ZoomableImage } from "@/components/ui/zoomable-image";
 *
 * <ZoomableImage
 *   src="/img/my-photo.jpeg"
 *   alt="Description"
 *   aspectRatio="849/1259"          // optional – defaults to "1/1"
 *   className="rounded-2xl"         // optional – applied to the thumbnail container
 *   sizes="(max-width: 640px) 90vw, 600px"  // optional – passed to <Image>
 * />
 *
 * Props:
 * ------
 * src          – image path (required)
 * alt          – alt text (required)
 * aspectRatio  – CSS aspect-ratio string, e.g. "16/9", "849/1259" (default "1/1")
 * className    – extra Tailwind classes on the thumbnail wrapper
 * sizes        – next/image sizes hint for the lightbox image
 */

import { useState } from "react";
import Image from "next/image";
import { ZoomIn } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface ZoomableImageProps {
  src: string;
  alt: string;
  aspectRatio?: string;
  className?: string;
  sizes?: string;
}

export function ZoomableImage({
  src,
  alt,
  aspectRatio = "1/1",
  className = "",
  sizes = "(max-width: 640px) 90vw, 600px",
}: ZoomableImageProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Thumbnail */}
      <div
        className={`relative w-full cursor-zoom-in overflow-hidden rounded-2xl group/zoom ${className}`}
        style={{ aspectRatio }}
        onClick={() => setOpen(true)}
      >
        <Image
          src={src}
          alt={alt}
          fill
          className="object-cover rounded-2xl border-[3px] border-background shadow-lg"
        />
        {/* Zoom hint overlay */}
        <div className="absolute bottom-3 right-3 z-10 bg-background/80 backdrop-blur-sm rounded-full p-1.5 opacity-0 group-hover/zoom:opacity-100 transition-opacity">
          <ZoomIn className="w-4 h-4 text-primary" />
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm sm:max-w-md p-0 overflow-hidden bg-transparent border-none shadow-none">
          <div className="relative w-full" style={{ aspectRatio }}>
            <Image
              src={src}
              alt={alt}
              fill
              className="object-cover rounded-2xl"
              sizes={sizes}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
